import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { ExpertContext, ProfileContext } from './contexts';
import { shouldFetchData, status } from '../../data/fetchStatus';
import Services from '../Services/services';
import useToast from '../Toasts/useToast';
import {
	ToastHeaders,
	ToastThemes,
	ToastMessages,
} from '../Toasts/ToastOptions/toastOptions';

function ExpertProvider() {
	const profileContext = useContext(ProfileContext);
	const enqueueToast = useToast();

	// quotes are the same than client because they update the same row
	const [clientExpertRelations, setClientExpertRelations] = useState([]);
	const [clientExpertRelationsFetched, setClientExpertRelationsFetched] =
		useState(status.DEFAULT);

	const [appointments, setAppointments] = useState([]);
	const [appointmentsFetched, setAppointmentsFetched] = useState(
		status.DEFAULT
	);

	const PopulateClientExpertRelations = useCallback(
		async (rels) => {
			for (const rel of rels) {
				try {
					const promises = [];

					promises.push(Services.profileService.clientsByIDs([rel?.clientId]));
					promises.push(
						Services.quoteService.quoteByRelation(rel?.clientId, rel?.expertId)
					);
					promises.push(
						Services.profileService.expertCommissionByRelationId(rel?.id)
					);

					const result = await Promise.all(promises);

					const client = result[0].length ? result[0][0] : {};
					const quote = result[1].length ? result[1][0] : {};
					const expertCommission = result[2].length ? result[2][0] : {};

					rel.client = client;
					rel.quote = quote;
					rel.expertCommission = expertCommission;
				} catch (error) {
					enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
						theme: ToastThemes.toastThemes.ERROR_OCCURED,
						header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
						duration: 3000,
					});
				}
			}

			setClientExpertRelations(rels);
		},
		[enqueueToast]
	);

	const fetchClientExpertRelations = useCallback(async () => {
		if (
			profileContext?.expert?.id === undefined ||
			clientExpertRelationsFetched === status.FETCHED
		)
			return;

		setClientExpertRelationsFetched(status.FETCHED);

		try {
			const data = await Services.quoteService.clientExpertRelationByExpertID(
				profileContext?.expert?.id
			);

			if (data.length === 0) {
				return;
			}

			// setClientExpertRelations(data);

			PopulateClientExpertRelations(data);
		} catch (error) {
			enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
				theme: ToastThemes.toastThemes.ERROR_OCCURED,
				header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
				duration: 3000,
			});
		}
	}, [
		profileContext?.expert?.id,
		clientExpertRelationsFetched,
		enqueueToast,
		PopulateClientExpertRelations,
	]);

	const fetchAppointments = useCallback(async () => {
		if (
			profileContext?.expert?.id === undefined ||
			appointmentsFetched === status.FETCHED
		)
			return;

		setAppointmentsFetched(status.FETCHED);

		try {
			const data = await Services.appointmentService.appointmentsByExpertID(
				profileContext?.expert?.id,
				true
			);

			if (data.length === 0) {
				return;
			}

			setAppointments(
				data.filter(
					(appointment) =>
						appointment?.acceptedByExpert && appointment?.acceptedByClient
				)
			);
		} catch (error) {
			enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
				theme: ToastThemes.toastThemes.ERROR_OCCURED,
				header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
				duration: 3000,
			});
		}
	}, [profileContext?.expert?.id, appointmentsFetched, enqueueToast]);

	useEffect(() => {
		if (shouldFetchData(clientExpertRelationsFetched))
			fetchClientExpertRelations();
		if (shouldFetchData(appointmentsFetched)) fetchAppointments();
	}, [
		clientExpertRelationsFetched,
		appointmentsFetched,
		fetchClientExpertRelations,
		fetchAppointments,
	]);

	function getClientExpertAppointment(searchedClient, searchedExpert) {
		return appointments.filter(
			({ clientId, expertId }) =>
				clientId === searchedClient && expertId === searchedExpert
		);
	}

	function getClientExpertRelationsByStatus(status) {
		return clientExpertRelations.filter((clientExpertRelation) =>
			status.includes(clientExpertRelation?.status)
		);
	}

	function getRelationByIds(clientExpertRelationId) {
		const relation = clientExpertRelations?.find(
			(rel) => rel.id === clientExpertRelationId
		);

		return relation;
	}

	return (
		<ExpertContext.Provider
			value={{
				expertId: profileContext.expert.id,
				expert: profileContext.expert,
				clientExpertRelations,
				appointments,
				getClientExpertRelationsByStatus,
				getClientExpertAppointment,
				getRelationByIds,
				refreshClientExpertRelations: () =>
					setClientExpertRelationsFetched(status.REFRESH),
				refreshAppointments: () => setAppointmentsFetched(status.REFRESH),
			}}
		>
			<Outlet />
		</ExpertContext.Provider>
	);
}

export default ExpertProvider;
